import React, { useEffect, useMemo, useState } from 'react';
import { supabase } from './supabase';
import { Printer, Flame, CalendarClock, PackageX, Undo2, Scissors, PackageCheck, AlertTriangle, X, Store, Truck, CheckCircle2, AlertCircle, RotateCcw } from 'lucide-react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { loadPdfImages, drawImageInCell } from './pdfImages';
import { logActivity } from './logger';
import { Thumb } from './ui/Thumb';
import { cn } from './ui/Table';
// Componentes de uiarc.dev (ver src/ui/arc)
import { Button } from './ui/arc/button';
import { Badge } from './ui/arc/badge';
import { Input } from './ui/arc/input';
import { MetricCard } from './ui/arc/metric-card';
import { EmptyState } from './ui/arc/empty-state';
import { SearchField } from './ui/arc/search-field';
import SegmentedControl from './ui/arc/segmented-control';
import { Dialog, DialogContent } from './ui/arc/dialog';
import { CortesPorColor } from './CortesPorColor';
import { applyCut, CutRef } from './cutByColor';

// Taller — qué hacer primero y qué después (migr 098, 07/10/2026).
//
// Reglas acordadas:
//   · La PRIORIDAD sale de la fecha de listo (ready_by = fecha de EMPAQUETADO):
//     urgentes primero, después la fecha más próxima, después la compra más vieja.
//   · ready_by_kind 'promised' = comprometida con el cliente (o límite de ML): se ve distinta.
//   · Atrasado = hoy > ready_by y no empaquetado. Bloqueado = falta insumo (con motivo).
//   · Transferencia sin pagar entra igual a la cola, con badge "Sin pagar".
//
// Cambio de estado: es un UPDATE simple. El WhatsApp de "listo para retirar" al empaquetar
// lo manda la cola del servidor (trigger orders_pickup_notification → process-pickup-
// notifications), que ya excluye ML, transferencias impagas y lo que no es retiro en el
// local, y deduplica contra whatsapp_logs. No se repite esa lógica acá.
//
// Comandera: UNA hoja por pedido. "Imprimir nuevos" imprime los que nunca se imprimieron y
// registra quién/cuándo + una firma de los ítems. Reimprimir sale con "COPIA" y motivo. Si
// el pedido cambió después de imprimir, o se canceló estando impreso, la vista lo avisa.

interface Item { name?: string; quantity?: number | string; tn_variant_id?: number; tn_product_id?: number; is_cut?: boolean }
interface WOrder {
  id: number; tiendanube_id: string | null; source: string | null; status: string;
  products: Item[] | null; contact_name: string | null; created_at: string;
  ready_by: string | null; ready_by_kind: 'auto' | 'promised' | null; ready_by_original: string | null;
  is_urgent: boolean | null; urgent_note: string | null; delay_reason: string | null;
  workshop_printed_at: string | null; workshop_printed_by: string | null;
  workshop_print_count: number | null; workshop_print_sig: string | null;
  delivery_type: string | null; delivery_address: string | null; delivery_unit?: string | null; shipping_option: string | null;
  note: string | null; owner_note: string | null; payment_status: string | null; gateway_name: string | null;
}

const COLS = 'id, tiendanube_id, source, status, products, contact_name, created_at, ready_by, ready_by_kind, ready_by_original, '
  + 'is_urgent, urgent_note, delay_reason, workshop_printed_at, workshop_printed_by, workshop_print_count, workshop_print_sig, '
  + 'delivery_type, delivery_address, delivery_unit, shipping_option, note, owner_note, payment_status, gateway_name';

// Mismo criterio que getOrderDisplayId de App.tsx / pdfGenerator.ts.
function displayId(o: { source?: string | null; tiendanube_id?: string | null; id: number }): string {
  if (o.source === 'woocommerce' && o.tiendanube_id) return `BT-${o.tiendanube_id.replace(/^(WC-|BT-)/i, '')}`;
  if ((o.source === 'tiendanube' || o.source === 'mercadolibre') && o.tiendanube_id) return String(o.tiendanube_id);
  return `K-${o.id}`;
}
const fmtNum = (o: WOrder) => { const d = displayId(o); return d.startsWith('K-') ? d : `#${d}`; };

// Fecha de HOY en Argentina (YYYY-MM-DD): las fechas de listo son días corridos en hora AR.
const todayAR = () => new Date().toLocaleDateString('en-CA', { timeZone: 'America/Argentina/Buenos_Aires' });
const dayDiff = (iso: string | null) => (iso ? Math.round((Date.parse(iso) - Date.parse(todayAR())) / 86400000) : null);
const fmtDay = (iso: string | null) => {
  if (!iso) return 'Sin fecha';
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('es-AR', { weekday: 'short', day: '2-digit', month: '2-digit' });
};

const itemsOf = (o: WOrder): Item[] => (Array.isArray(o.products) ? o.products : []);
// Firma estable de los ítems (nombre + cantidad): si cambia después de imprimir, la hoja quedó vieja.
function itemsSig(o: WOrder): string {
  const s = itemsOf(o).map(i => `${(i.name || '').trim()}|${Number(i.quantity) || 0}`).sort().join('¦');
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) | 0;
  return (h >>> 0).toString(16);
}
const isTransferUnpaid = (o: WOrder) =>
  /transferencia/i.test(o.gateway_name || '') && o.payment_status !== 'paid';
const isPickup = (o: WOrder) => o.delivery_type === 'retiro';
const changedAfterPrint = (o: WOrder) => !!o.workshop_printed_at && !!o.workshop_print_sig && o.workshop_print_sig !== itemsSig(o);

// Prioridad: urgente → fecha más próxima (sin fecha al final) → compra más vieja.
function byPriority(a: WOrder, b: WOrder) {
  if (!!a.is_urgent !== !!b.is_urgent) return a.is_urgent ? -1 : 1;
  if (a.ready_by !== b.ready_by) {
    if (!a.ready_by) return 1;
    if (!b.ready_by) return -1;
    return a.ready_by < b.ready_by ? -1 : 1;
  }
  return a.created_at < b.created_at ? -1 : 1;
}

type Bucket = 'late' | 'today' | 'tomorrow' | 'week' | 'later';
const BUCKETS: { key: Bucket; title: string }[] = [
  { key: 'late', title: 'Atrasados' },
  { key: 'today', title: 'Para hoy' },
  { key: 'tomorrow', title: 'Para mañana' },
  { key: 'week', title: 'Esta semana' },
  { key: 'later', title: 'Más adelante' },
];
function bucketOf(o: WOrder): Bucket {
  const d = dayDiff(o.ready_by);
  if (d == null) return 'later';
  if (d < 0) return 'late';
  if (d === 0) return 'today';
  if (d === 1) return 'tomorrow';
  if (d <= 7) return 'week';
  return 'later';
}

export function Taller() {
  const [orders, setOrders] = useState<WOrder[]>([]);
  const [cancelledPrinted, setCancelledPrinted] = useState<WOrder[]>([]);
  const [images, setImages] = useState<{ variant: Map<number, string>; product: Map<number, string> }>({ variant: new Map(), product: new Map() });
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState<string | null>(null);
  const [okMsg, setOkMsg] = useState<string | null>(null);
  const [stage, setStage] = useState<'all' | 'cut' | 'pack' | 'blocked'>('all');
  const [q, setQ] = useState('');
  const [printing, setPrinting] = useState(false);
  const [busy, setBusy] = useState<number | null>(null);
  const [reprog, setReprog] = useState<{ o: WOrder; date: string; promised: boolean } | null>(null);
  const [me, setMe] = useState<string | undefined>(undefined);
  // Vista: la cola de pedidos o "Cortes por color" (01/11/2026). Se recuerda en la URL (?vista=cortes).
  const [view, setView] = useState<'pedidos' | 'cortes'>(() => (new URLSearchParams(window.location.search).get('vista') === 'cortes' ? 'cortes' : 'pedidos'));
  const changeView = (v: 'pedidos' | 'cortes') => {
    setView(v);
    const u = new URL(window.location.href);
    if (v === 'cortes') u.searchParams.set('vista', 'cortes'); else u.searchParams.delete('vista');
    window.history.replaceState(window.history.state, '', u.toString());
  };

  useEffect(() => { supabase.auth.getUser().then(({ data }) => setMe(data.user?.email || undefined)); }, []);

  async function load() {
    setLoading(true);
    const since = new Date(Date.now() - 14 * 86400000).toISOString();
    const [open, canc] = await Promise.all([
      supabase.from('orders').select(COLS).in('status', ['nueva', 'falta_insumo', 'cortado']).is('deleted_at', null),
      // Canceladas que estaban IMPRESAS: el taller tiene que sacar la hoja.
      supabase.from('orders').select(COLS).eq('status', 'cancelada').not('workshop_printed_at', 'is', null).gt('updated_at', since),
    ]);
    if (open.error) setMsg('No se pudieron cargar los pedidos: ' + open.error.message);
    const list = ((open.data || []) as unknown as WOrder[]).filter(o => !['stock', 'presupuesto'].includes(o.source || ''));
    setOrders(list);
    setCancelledPrinted((canc.data || []) as unknown as WOrder[]);
    setLoading(false);
    // Fotos de los ítems: por variante (vía su producto) o por producto.
    const vIds = new Set<number>(), pIds = new Set<number>();
    list.forEach(o => itemsOf(o).forEach(i => { if (i.tn_variant_id) vIds.add(Number(i.tn_variant_id)); if (i.tn_product_id) pIds.add(Number(i.tn_product_id)); }));
    const variant = new Map<number, string>(), product = new Map<number, string>();
    if (vIds.size) {
      const { data: vars } = await supabase.from('tn_variants').select('id, product_id').in('id', Array.from(vIds));
      (vars || []).forEach((v: any) => pIds.add(Number(v.product_id)));
      if (pIds.size) {
        const { data: prods } = await supabase.from('tn_products').select('id, image_url').in('id', Array.from(pIds));
        (prods || []).forEach((p: any) => { if (p.image_url) product.set(Number(p.id), p.image_url); });
      }
      (vars || []).forEach((v: any) => { const u = product.get(Number(v.product_id)); if (u) variant.set(Number(v.id), u); });
    } else if (pIds.size) {
      const { data: prods } = await supabase.from('tn_products').select('id, image_url').in('id', Array.from(pIds));
      (prods || []).forEach((p: any) => { if (p.image_url) product.set(Number(p.id), p.image_url); });
    }
    setImages({ variant, product });
  }
  useEffect(() => { load(); }, []);

  const imgOf = (i: Item) => (i.tn_variant_id && images.variant.get(Number(i.tn_variant_id)))
    || (i.tn_product_id && images.product.get(Number(i.tn_product_id))) || null;

  const sorted = useMemo(() => [...orders].sort(byPriority), [orders]);
  const active = sorted.filter(o => o.status !== 'falta_insumo');
  const blocked = sorted.filter(o => o.status === 'falta_insumo');
  const unprinted = sorted.filter(o => !o.workshop_printed_at);
  const changed = sorted.filter(changedAfterPrint);
  const count = (b: Bucket) => active.filter(o => bucketOf(o) === b).length;

  const matches = (o: WOrder) => {
    const t = q.trim().toLowerCase();
    if (!t) return true;
    return `${displayId(o)} ${o.contact_name || ''} ${itemsOf(o).map(i => i.name).join(' ')}`.toLowerCase().includes(t);
  };
  const visible = (stage === 'blocked' ? blocked : active.filter(o => stage === 'all' || (stage === 'cut' ? o.status === 'nueva' : o.status === 'cortado'))).filter(matches);

  // ── Acciones ──────────────────────────────────────────────────────────────
  async function patch(o: WOrder, p: Partial<WOrder>, action: string, details: any = {}) {
    setBusy(o.id);
    const prev = orders;
    setOrders(os => os.map(x => (x.id === o.id ? { ...x, ...p } : x)));
    const { error } = await supabase.from('orders').update(p).eq('id', o.id);
    setBusy(null);
    if (error) { setOrders(prev); setMsg('No se pudo guardar: ' + error.message); return false; }
    logActivity(me, action, { order_id: o.id, display_id: displayId(o), ...details });
    return true;
  }

  // Cortes por color → marcar piezas cortadas (02/11/2026). Mismo criterio que el tilde de la card
  // (toggleProductCut en App.tsx): is_cut por ítem y, si quedan todos cortados y estaba en 'nueva',
  // el pedido pasa a 'cortado'. En sets de varios colores se guarda qué colores ya se cortaron.
  async function markCut(refs: CutRef[]): Promise<boolean> {
    const byOrder = new Map<number, CutRef[]>();
    refs.forEach(r => byOrder.set(r.orderId, [...(byOrder.get(r.orderId) || []), r]));
    let ok = true; const auto: string[] = [];
    for (const [id, rs] of Array.from(byOrder.entries())) {
      const o = orders.find(x => x.id === id); if (!o) continue;
      const products = applyCut(itemsOf(o) as any, rs) as Item[];
      const allCut = products.length > 0 && products.every(p => p.is_cut);
      const p: any = { products };
      if (allCut && o.status === 'nueva') { p.status = 'cortado'; auto.push(fmtNum(o)); }
      const { error } = await supabase.from('orders').update(p).eq('id', id);
      if (error) { ok = false; setMsg(`No se pudo guardar ${fmtNum(o)}: ${error.message}`); continue; }
      setOrders(os => os.map(x => (x.id === id ? { ...x, ...p } : x)));
      logActivity(me, p.status ? 'ORDEN_CORTADA_AUTO' : 'PRODUCTO_CORTADO', {
        order_id: id, display_id: displayId(o),
        note: `Cortes por color: ${rs.map(r => `${itemsOf(o)[r.idx]?.name} (${r.color})`).join(' · ')}${p.status ? ' → pedido completo, pasa a CORTADO' : ''}`,
      });
    }
    if (ok) setOkMsg(`Marcado como cortado.${auto.length ? ` Pasaron a CORTADO: ${auto.join(', ')}.` : ''}`);
    return ok;
  }

  async function advance(o: WOrder) {
    const next = o.status === 'nueva' ? 'cortado' : 'empaquetado';
    if (next === 'empaquetado') {
      const aviso = isPickup(o) && o.source !== 'mercadolibre'
        ? (isTransferUnpaid(o) ? '\n\nEs retiro pero está SIN PAGAR: no se le avisa al cliente hasta que pague.' : '\n\nEs retiro en el local: al cliente le llega el WhatsApp de "listo para retirar".')
        : '';
      if (!window.confirm(`¿Marcar ${fmtNum(o)} como EMPAQUETADO?${aviso}`)) return;
    }
    const ok = await patch(o, { status: next } as any, 'TALLER_ESTADO', { from: o.status, to: next });
    if (ok && next === 'empaquetado') {
      setOrders(os => os.filter(x => x.id !== o.id));   // sale de la cola del taller
      setOkMsg(`${fmtNum(o)} empaquetado.`);
    }
  }

  async function toggleUrgent(o: WOrder) {
    if (o.is_urgent) { await patch(o, { is_urgent: false, urgent_note: null }, 'TALLER_URGENTE_OFF'); return; }
    const note = window.prompt(`¿Por qué ${fmtNum(o)} es urgente? (opcional, lo ve el taller)`, '');
    if (note === null) return;
    await patch(o, { is_urgent: true, urgent_note: note.trim() || null }, 'TALLER_URGENTE_ON', { note });
  }

  async function blockMissing(o: WOrder) {
    const reason = window.prompt(`¿Qué falta para ${fmtNum(o)}? (ej: acrílico azul 3mm)`, o.delay_reason || '');
    if (reason === null) return;
    if (!reason.trim()) { setMsg('Escribí qué insumo falta: es lo que ve quien compra.'); return; }
    await patch(o, { status: 'falta_insumo', delay_reason: reason.trim() } as any, 'TALLER_FALTA_INSUMO', { reason });
  }

  // Volver a la cola: al estado en el que estaba antes de bloquearse (historial), o 'nueva'.
  async function unblock(o: WOrder) {
    const { data } = await supabase.from('order_status_history').select('from_status')
      .eq('order_id', o.id).eq('to_status', 'falta_insumo').order('changed_at', { ascending: false }).limit(1).maybeSingle();
    const back = data?.from_status === 'cortado' ? 'cortado' : 'nueva';
    await patch(o, { status: back } as any, 'TALLER_DESBLOQUEO', { to: back });
  }

  async function saveReprog() {
    if (!reprog?.date) return;
    const ok = await patch(reprog.o, { ready_by: reprog.date, ready_by_kind: reprog.promised ? 'promised' : 'auto' },
      'TALLER_REPROGRAMAR', { from: reprog.o.ready_by, to: reprog.date, promised: reprog.promised });
    if (ok) setReprog(null);
  }

  // ── Comandera ─────────────────────────────────────────────────────────────
  async function buildPdf(list: WOrder[], copy?: { n: number; reason: string }) {
    const doc = new jsPDF({ unit: 'mm', format: 'a4' });
    const urls = list.flatMap(o => itemsOf(o).map(imgOf));
    const imgs = await loadPdfImages(urls, 320);
    const W = 210, M = 14;
    const INK: [number, number, number] = [28, 25, 23];
    const MUTED: [number, number, number] = [120, 113, 108];
    const LINE: [number, number, number] = [225, 222, 220];
    const BRAND: [number, number, number] = [148, 30, 33];
    const stamp = new Date().toLocaleString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });

    list.forEach((o, idx) => {
      if (idx > 0) doc.addPage();
      const pageStart = doc.getNumberOfPages();
      if (copy) {
        // marca de agua: se dibuja ANTES del contenido (la tabla es 'plain', sin fondo)
        doc.setFont('helvetica', 'bold'); doc.setFontSize(110); doc.setTextColor(238, 232, 230);
        doc.text('COPIA', 40, 230, { angle: 40 });
      }
      // Encabezado: nº grande + fecha de listo grande
      doc.setFillColor(...BRAND); doc.rect(0, 0, W, 3, 'F');
      doc.setFont('helvetica', 'bold'); doc.setFontSize(34); doc.setTextColor(...INK);
      doc.text(fmtNum(o), M, 22);
      doc.setFont('helvetica', 'normal'); doc.setFontSize(12); doc.setTextColor(...MUTED);
      doc.text(o.contact_name || '—', M, 30);
      doc.setFontSize(8); doc.text('LISTO PARA (EMPAQUETADO)', W - M, 11, { align: 'right' });
      doc.setFont('helvetica', 'bold'); doc.setFontSize(22); doc.setTextColor(...INK);
      doc.text(fmtDay(o.ready_by), W - M, 21, { align: 'right' });
      // etiquetas: COMPROMETIDA / URGENTE (cajas de color)
      const tags: [string, [number, number, number]][] = [];
      if (o.is_urgent) tags.push(['URGENTE', BRAND]);
      if (o.ready_by_kind === 'promised') tags.push(['FECHA COMPROMETIDA', [180, 83, 9]]);
      if (isTransferUnpaid(o)) tags.push(['SIN PAGAR', [100, 100, 100]]);
      let tx = W - M;
      doc.setFontSize(8);
      tags.forEach(([t, c]) => {
        const w = doc.getTextWidth(t) + 5;
        doc.setFillColor(...c); doc.roundedRect(tx - w, 25, w, 6, 1, 1, 'F');
        doc.setTextColor(255); doc.text(t, tx - w / 2, 29.2, { align: 'center' });
        tx -= w + 2;
      });
      // Entrega + etapa
      doc.setDrawColor(...LINE); doc.setFillColor(250, 250, 249);
      doc.roundedRect(M, 35, W - 2 * M, 13, 2, 2, 'FD');
      doc.setFont('helvetica', 'bold'); doc.setFontSize(10.5); doc.setTextColor(...INK);
      const entrega = isPickup(o) ? 'RETIRA EN EL LOCAL' : 'ENVÍO';
      doc.text(entrega, M + 4, 42.8);
      doc.setFont('helvetica', 'normal'); doc.setFontSize(8.5); doc.setTextColor(...MUTED);
      const detalle = [o.shipping_option, isPickup(o) ? null : o.delivery_address, isPickup(o) || !o.delivery_unit ? null : `Piso/Depto ${o.delivery_unit}`].filter(Boolean).join(' · ');
      if (detalle) doc.text(doc.splitTextToSize(detalle, 120)[0], M + 4 + doc.getTextWidth(entrega) + 12, 42.8);
      doc.text(`Etapa: ${o.status === 'nueva' ? 'por cortar' : o.status === 'cortado' ? 'por empaquetar' : o.status}`, W - M - 4, 42.8, { align: 'right' });

      // Ítems: foto + nombre + cantidad grande + casillero
      const items = itemsOf(o);
      const PHOTO = items.length > 8 ? 13 : 18;
      autoTable(doc, {
        startY: 53,
        margin: { left: M, right: M, top: 14, bottom: 22 },
        head: [['Foto', 'Producto', 'Cant.', 'Hecho']],
        body: items.map(() => ['', '', '', '']),
        theme: 'plain',
        styles: { fontSize: 10, textColor: INK, valign: 'middle', minCellHeight: PHOTO + 4, cellPadding: 2 },
        headStyles: { fontSize: 7.5, fontStyle: 'bold', textColor: MUTED, minCellHeight: 7, fillColor: [245, 245, 244] },
        columnStyles: { 0: { cellWidth: PHOTO + 5 }, 1: { cellWidth: 'auto' }, 2: { cellWidth: 24 }, 3: { cellWidth: 16 } },
        rowPageBreak: 'avoid',
        didParseCell: (d: any) => { if (d.section === 'head' && d.column.index >= 2) d.cell.styles.halign = 'center'; },
        didDrawCell: (d: any) => {
          if (d.section !== 'body') return;
          const c = d.cell, it = items[d.row.index];
          if (d.column.index === 0) {
            doc.setDrawColor(...LINE); doc.setLineWidth(0.2); doc.line(M, c.y + c.height, W - M, c.y + c.height);
            const bx = c.x + (c.width - PHOTO) / 2, by = c.y + (c.height - PHOTO) / 2;
            const u = imgOf(it); const im = u ? imgs.get(u) : null;
            if (im) drawImageInCell(doc, im, { x: bx, y: by, width: PHOTO, height: PHOTO }, 0.5);
            doc.roundedRect(bx, by, PHOTO, PHOTO, 1.2, 1.2, 'S');
          }
          if (d.column.index === 1 && it) {
            const lines = doc.splitTextToSize(it.name || '—', c.width - 4).slice(0, 3);
            doc.setFont('helvetica', 'bold'); doc.setFontSize(10.5); doc.setTextColor(...INK);
            doc.text(lines, c.x + 2, c.y + c.height / 2 - (lines.length - 1) * 2.2 + 1.2);
          }
          if (d.column.index === 2 && it) {
            doc.setFont('helvetica', 'bold'); doc.setFontSize(18); doc.setTextColor(...INK);
            doc.text(String(Number(it.quantity) || 1), c.x + c.width / 2, c.y + c.height / 2 + 2.5, { align: 'center' });
          }
          if (d.column.index === 3) {
            doc.setDrawColor(150, 146, 143); doc.setLineWidth(0.35);
            doc.roundedRect(c.x + c.width / 2 - 3, c.y + c.height / 2 - 3, 6, 6, 0.8, 0.8, 'S');
          }
        },
      });

      // Notas (cliente + internas)
      let y = (doc as any).lastAutoTable.finalY + 6;
      const notes: [string, string][] = [];
      if (o.note?.trim()) notes.push(['NOTA DEL CLIENTE', o.note.trim()]);
      if (o.owner_note?.trim()) notes.push(['NOTA INTERNA', o.owner_note.trim()]);
      if (o.urgent_note?.trim()) notes.push(['POR QUÉ ES URGENTE', o.urgent_note.trim()]);
      notes.forEach(([t, body]) => {
        const txt = doc.splitTextToSize(body, W - 2 * M - 8).slice(0, 6);
        const h = 9 + txt.length * 4.6;
        if (y + h > 270) { doc.addPage(); y = 18; }
        doc.setDrawColor(...LINE); doc.setFillColor(250, 250, 249); doc.roundedRect(M, y, W - 2 * M, h, 2, 2, 'FD');
        doc.setFont('helvetica', 'bold'); doc.setFontSize(7.5); doc.setTextColor(...MUTED); doc.text(t, M + 4, y + 5.5);
        doc.setFont('helvetica', 'normal'); doc.setFontSize(10); doc.setTextColor(...INK); doc.text(txt, M + 4, y + 10.5);
        y += h + 3;
      });

      // Pie en cada hoja de ESTE pedido
      for (let pg = pageStart; pg <= doc.getNumberOfPages(); pg++) {
        doc.setPage(pg);
        doc.setDrawColor(...LINE); doc.setLineWidth(0.2); doc.line(M, 284, W - M, 284);
        doc.setFont('helvetica', 'normal'); doc.setFontSize(7.5); doc.setTextColor(...MUTED);
        doc.text(`${fmtNum(o)} · compra ${new Date(o.created_at).toLocaleDateString('es-AR')}${o.ready_by_original && o.ready_by_original !== o.ready_by ? ` · fecha original ${fmtDay(o.ready_by_original)}` : ''}`, M, 289);
        doc.text(copy ? `REIMPRESIÓN ${copy.n} — ${copy.reason} · ${stamp}` : `Impreso ${stamp}${me ? ' · ' + me : ''}`, W - M, 289, { align: 'right' });
      }
    });
    return doc;
  }

  async function printNew() {
    const list = unprinted;
    if (!list.length) return;
    setPrinting(true); setMsg(null);
    try {
      const doc = await buildPdf(list);
      doc.save(`comandera-${todayAR()}.pdf`);
      const now = new Date().toISOString();
      const results = await Promise.all(list.map(o => supabase.from('orders').update({
        workshop_printed_at: now, workshop_printed_by: me || null, workshop_print_count: 1, workshop_print_sig: itemsSig(o),
      }).eq('id', o.id)));
      const failed = results.filter(r => r.error).length;
      setOrders(os => os.map(o => (list.some(x => x.id === o.id)
        ? { ...o, workshop_printed_at: now, workshop_printed_by: me || null, workshop_print_count: 1, workshop_print_sig: itemsSig(o) } : o)));
      logActivity(me, 'TALLER_COMANDERA', { pedidos: list.map(displayId) });
      if (failed) setMsg(`Se imprimió, pero ${failed} pedido(s) no quedaron marcados como impresos. Recargá antes de volver a imprimir.`);
      else setOkMsg(`Comandera lista: ${list.length} hoja(s), una por pedido, en orden de prioridad.`);
    } catch (e: any) { setMsg('No se pudo generar la comandera: ' + (e?.message || e)); }
    setPrinting(false);
  }

  async function reprint(o: WOrder) {
    const reason = window.prompt(`Reimprimir ${fmtNum(o)}: sale marcada como COPIA. ¿Por qué? (ej: se perdió, cambió el pedido)`,
      changedAfterPrint(o) ? 'cambió el pedido' : '');
    if (reason === null) return;
    if (!reason.trim()) { setMsg('Escribí el motivo de la reimpresión.'); return; }
    const n = (o.workshop_print_count || 1) + 1;
    setBusy(o.id);
    try {
      const doc = await buildPdf([o], { n, reason: reason.trim() });
      doc.save(`comandera-${displayId(o)}-copia${n}.pdf`);
      await patch(o, { workshop_printed_at: new Date().toISOString(), workshop_printed_by: me || null, workshop_print_count: n, workshop_print_sig: itemsSig(o) },
        'TALLER_REIMPRESION', { n, reason });
    } catch (e: any) { setMsg('No se pudo reimprimir: ' + (e?.message || e)); }
    setBusy(null);
  }

  // Cancelado e impreso: el taller confirma que sacó la hoja → deja de figurar.
  async function dismissCancelled(o: WOrder) {
    const { error } = await supabase.from('orders').update({ workshop_printed_at: null }).eq('id', o.id);
    if (error) { setMsg('No se pudo marcar: ' + error.message); return; }
    setCancelledPrinted(cs => cs.filter(x => x.id !== o.id));
    logActivity(me, 'TALLER_HOJA_RETIRADA', { order_id: o.id, display_id: displayId(o) });
  }

  // ── Render ────────────────────────────────────────────────────────────────
  const dateTone = (o: WOrder) => {
    const b = bucketOf(o);
    return b === 'late' ? 'text-[var(--danger)]' : b === 'today' || b === 'tomorrow' ? 'text-[var(--warning)]' : 'text-[var(--success)]';
  };

  const Row = ({ o }: { o: WOrder }) => {
    const items = itemsOf(o);
    const d = dayDiff(o.ready_by);
    return (
      <li className={cn('group grid gap-3 border-b border-[var(--border-subtle)] px-4 py-3 last:border-0 lg:grid-cols-[9rem_1fr_auto] lg:items-center',
        o.is_urgent && 'bg-[color-mix(in_oklab,var(--brand)_5%,var(--card))]')}>
        {/* Fecha + número */}
        <div className="flex items-center gap-3 lg:block">
          <div className={cn('text-[length:var(--text-base)] font-semibold tabular-nums', dateTone(o))}>{fmtDay(o.ready_by)}</div>
          <div className="text-[length:var(--text-xs)] text-faint">
            {d == null ? '' : d < 0 ? `${-d} día(s) de atraso` : d === 0 ? 'hoy' : d === 1 ? 'mañana' : `en ${d} días`}
          </div>
          <div className="mt-0.5 text-[length:var(--text-sm)] font-semibold text-ink">{fmtNum(o)}</div>
        </div>
        {/* Cliente + ítems + badges */}
        <div className="min-w-0">
          <div className="mb-1.5 flex flex-wrap items-center gap-1.5">
            <span className="mr-1 truncate text-[length:var(--text-sm)] font-medium text-ink">{o.contact_name || '—'}</span>
            {o.is_urgent && <Badge size="sm" tone="danger" icon={<Flame size={11} />}>Urgente</Badge>}
            {o.ready_by_kind === 'promised' && <Badge size="sm" tone="warning">Comprometida</Badge>}
            {isTransferUnpaid(o) && <Badge size="sm">Sin pagar</Badge>}
            <Badge size="sm" icon={isPickup(o) ? <Store size={11} /> : <Truck size={11} />}>{isPickup(o) ? 'Retiro' : 'Envío'}</Badge>
            <Badge size="sm" tone={o.status === 'cortado' ? 'info' : 'neutral'}>{o.status === 'nueva' ? 'Por cortar' : o.status === 'cortado' ? 'Por empaquetar' : 'Falta insumo'}</Badge>
            {!o.workshop_printed_at && <Badge size="sm" tone="info" icon={<Printer size={11} />}>Sin imprimir</Badge>}
            {changedAfterPrint(o) && <Badge size="sm" tone="warning" icon={<AlertTriangle size={11} />}>Cambió después de imprimir</Badge>}
          </div>
          {o.status === 'falta_insumo' && o.delay_reason && (
            <div className="mb-1.5 text-[length:var(--text-sm)] text-[var(--danger)]">Falta: {o.delay_reason}</div>
          )}
          {o.is_urgent && o.urgent_note && <div className="mb-1.5 text-[length:var(--text-xs)] text-soft">Urgente: {o.urgent_note}</div>}
          <ul className="m-0 flex list-none flex-wrap gap-2 p-0">
            {items.map((it, i) => (
              <li key={i} className="flex min-w-0 max-w-full items-center gap-2 rounded-[var(--radius-control)] border border-line py-1 pl-1 pr-2.5">
                <Thumb src={imgOf(it)} name={it.name} size="sm" />
                <span className="min-w-0 truncate text-[length:var(--text-xs)] text-ink sm:max-w-[22rem]" title={it.name}>{it.name}</span>
                <span className="shrink-0 text-[length:var(--text-sm)] font-semibold tabular-nums text-ink">×{Number(it.quantity) || 1}</span>
              </li>
            ))}
          </ul>
        </div>
        {/* Acciones */}
        <div className="flex flex-wrap items-center gap-1 lg:justify-end">
          {o.status === 'falta_insumo' ? (
            <Button variant="secondary" size="sm" disabled={busy === o.id} onClick={() => unblock(o)}><Undo2 size={14} /> Volver a la cola</Button>
          ) : (
            <Button size="sm" disabled={busy === o.id} onClick={() => advance(o)}>
              {o.status === 'nueva' ? <><Scissors size={14} /> Cortado</> : <><PackageCheck size={14} /> Empaquetado</>}
            </Button>
          )}
          <Button variant={o.is_urgent ? 'danger' : 'ghost'} size="sm" title={o.is_urgent ? 'Sacar urgente' : 'Marcar urgente (pasa adelante)'} aria-label="Urgente"
            onClick={() => toggleUrgent(o)}><Flame size={14} /></Button>
          <Button variant="ghost" size="sm" title="Reprogramar fecha de listo" aria-label="Reprogramar"
            onClick={() => setReprog({ o, date: o.ready_by || todayAR(), promised: o.ready_by_kind === 'promised' })}><CalendarClock size={14} /></Button>
          {o.status !== 'falta_insumo' && (
            <Button variant="ghost" size="sm" title="Falta insumo (bloquea el pedido)" aria-label="Falta insumo" onClick={() => blockMissing(o)}><PackageX size={14} /></Button>
          )}
          {o.workshop_printed_at && (
            <Button variant="ghost" size="sm" title={`Reimprimir (sale como COPIA) · impreso ${new Date(o.workshop_printed_at).toLocaleDateString('es-AR')}`} aria-label="Reimprimir"
              onClick={() => reprint(o)}><RotateCcw size={14} /></Button>
          )}
        </div>
      </li>
    );
  };

  const groups = stage === 'blocked'
    ? [{ key: 'blocked', title: 'Bloqueados — falta insumo', list: visible }]
    : BUCKETS.map(b => ({ key: b.key, title: b.title, list: visible.filter(o => bucketOf(o) === b.key) })).filter(g => g.list.length);

  return (
    <div className="tw w-full min-w-0 pb-12 pt-1">
      <header className="mb-5 flex flex-wrap items-end justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-[length:var(--text-2xl)] font-semibold leading-tight tracking-[var(--tracking-display)] text-ink">Taller</h1>
          <p className="mt-1 max-w-[72ch] text-[length:var(--text-sm)] text-soft">
            Qué hacer primero: urgentes, después la fecha de listo más próxima. La fecha es la de empaquetado; después sale por su canal.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <SegmentedControl label="Vista" value={view} onValueChange={v => changeView(v as any)}
            options={[{ value: 'pedidos', label: 'Pedidos' }, { value: 'cortes', label: 'Cortes por color' }]} />
          {view === 'pedidos' && (
            <Button loading={printing} disabled={!unprinted.length} onClick={printNew}
              title="Una hoja por pedido, en orden de prioridad. Solo los que nunca se imprimieron.">
              <Printer size={16} /> Imprimir nuevos ({unprinted.length})
            </Button>
          )}
        </div>
      </header>


      {okMsg && (
        <div role="status" className="mb-4 flex items-center gap-2 rounded-[var(--radius-control)] border border-[color-mix(in_oklab,var(--success)_30%,var(--border))] bg-[color-mix(in_oklab,var(--success)_8%,var(--card))] px-3 py-2 text-[length:var(--text-sm)] text-[var(--success)]">
          <CheckCircle2 size={16} className="shrink-0" /><span className="min-w-0 flex-1">{okMsg}</span>
          <Button variant="ghost" size="sm" aria-label="Cerrar" onClick={() => setOkMsg(null)}><X size={14} /></Button>
        </div>
      )}
      {msg && (
        <div role="alert" className="mb-4 flex items-center gap-2 rounded-[var(--radius-control)] border border-[color-mix(in_oklab,var(--danger)_30%,var(--border))] bg-[color-mix(in_oklab,var(--danger)_8%,var(--card))] px-3 py-2 text-[length:var(--text-sm)] text-[var(--danger)]">
          <AlertCircle size={16} className="shrink-0" /><span className="min-w-0 flex-1">{msg}</span>
          <Button variant="ghost" size="sm" aria-label="Cerrar" onClick={() => setMsg(null)}><X size={14} /></Button>
        </div>
      )}

      {view === 'cortes' ? (
        <CortesPorColor loading={loading} onCut={markCut}
          orders={orders.map(o => ({ id: o.id, num: fmtNum(o), status: o.status, payment_status: o.payment_status, products: o.products }))} />
      ) : (<>
      <section className="mb-5 grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-5">
        <MetricCard label="Atrasados" value={count('late')} locale="es-AR" context="Pasó su fecha y no están empaquetados" />
        <MetricCard label="Para hoy" value={count('today')} locale="es-AR" context="Tienen que quedar empaquetados hoy" />
        <MetricCard label="Para mañana" value={count('tomorrow')} locale="es-AR" context="Conviene adelantarlos hoy" />
        <MetricCard label="Bloqueados" value={blocked.length} locale="es-AR" context="Falta insumo: no se pueden terminar" />
        <MetricCard label="Sin imprimir" value={unprinted.length} locale="es-AR" context="Todavía no tienen su hoja en el taller" />
      </section>

      {/* Avisos que requieren acción física en el taller */}
      {cancelledPrinted.length > 0 && (
        <section className="mb-4 rounded-[var(--radius-surface)] border border-[color-mix(in_oklab,var(--danger)_35%,var(--border))] bg-card p-4">
          <div className="mb-2 flex items-center gap-2 text-[length:var(--text-sm)] font-semibold text-[var(--danger)]">
            <PackageX size={16} /> Sacar del taller — cancelados que ya estaban impresos
          </div>
          <ul className="m-0 list-none p-0">
            {cancelledPrinted.map(o => (
              <li key={o.id} className="flex items-center justify-between gap-2 border-b border-[var(--border-subtle)] py-2 last:border-0">
                <span className="text-[length:var(--text-sm)] text-ink"><b className="font-semibold">{fmtNum(o)}</b> · {o.contact_name}</span>
                <Button variant="secondary" size="sm" onClick={() => dismissCancelled(o)}>Ya saqué la hoja</Button>
              </li>
            ))}
          </ul>
        </section>
      )}
      {changed.length > 0 && (
        <div className="mb-4 flex items-center gap-2 rounded-[var(--radius-control)] border border-[color-mix(in_oklab,var(--warning)_35%,var(--border))] bg-[color-mix(in_oklab,var(--warning)_8%,var(--card))] px-3 py-2 text-[length:var(--text-sm)] text-[var(--warning)]">
          <AlertTriangle size={16} className="shrink-0" />
          <span>{changed.length} pedido(s) cambiaron después de imprimir su hoja: {changed.map(fmtNum).join(', ')}. Reimprimilos (↻) y tirá la hoja vieja.</span>
        </div>
      )}

      <section className="overflow-hidden rounded-[var(--radius-surface)] border border-line bg-card shadow-xs">
        <div className="flex flex-wrap items-center gap-2 border-b border-line p-3">
          <div className="min-w-[12rem] flex-1">
            <SearchField label="Buscar pedido" hideLabel value={q} onValueChange={setQ} placeholder="Buscar nº, cliente o producto…" />
          </div>
          <SegmentedControl label="Etapa" value={stage} onValueChange={v => setStage(v as any)}
            options={[
              { value: 'all', label: 'Todo' },
              { value: 'cut', label: 'Por cortar' },
              { value: 'pack', label: 'Por empaquetar' },
              { value: 'blocked', label: 'Bloqueados', accessory: blocked.length ? <Badge size="sm" tone="danger">{blocked.length}</Badge> : undefined },
            ]} />
        </div>

        {loading ? (
          <div className="p-4">{Array.from({ length: 5 }).map((_, i) => <div key={i} className="mb-3 h-16 animate-pulse rounded-[var(--radius-control)] bg-muted" />)}</div>
        ) : groups.length === 0 ? (
          q.trim() || stage !== 'all'
            ? <EmptyState title="Nada coincide" description="Ningún pedido pasa la búsqueda o el filtro de etapa."
                action={<Button variant="secondary" size="sm" onClick={() => { setQ(''); setStage('all'); }}>Ver todo</Button>} />
            : <EmptyState title="Taller al día" description="No hay pedidos pendientes de cortar o empaquetar." />
        ) : (
          groups.map(g => (
            <div key={g.key}>
              <div className={cn('flex items-center justify-between border-b border-line bg-muted px-4 py-2 text-[length:var(--text-xs)] font-semibold uppercase tracking-wide',
                g.key === 'late' || g.key === 'blocked' ? 'text-[var(--danger)]' : 'text-soft')}>
                <span>{g.title}</span><span className="tabular-nums">{g.list.length}</span>
              </div>
              <ul className="m-0 list-none p-0">{g.list.map(o => <Row key={o.id} o={o} />)}</ul>
            </div>
          ))
        )}
      </section>

      </>)}

      {/* Reprogramar: la fecha original se conserva (migr 098) para medir el atraso real. */}
      <Dialog open={!!reprog} onOpenChange={o => { if (!o) setReprog(null); }}>
        {reprog && (
          <DialogContent className="tw !w-[min(calc(100vw_-_2rem),440px)]" title={`Reprogramar ${fmtNum(reprog.o)}`}
            description={reprog.o.ready_by_original ? `Fecha original: ${fmtDay(reprog.o.ready_by_original)}. Se conserva para medir el atraso.` : undefined}>
            <div className="grid gap-4">
              <Input label="Nueva fecha de listo (empaquetado)" type="date" value={reprog.date}
                onChange={e => setReprog(r => r && { ...r, date: e.target.value })} />
              <label className="flex cursor-pointer items-center gap-2 text-[length:var(--text-sm)] text-ink">
                <input type="checkbox" className="h-4 w-4 accent-[var(--foreground)]" checked={reprog.promised}
                  onChange={e => setReprog(r => r && { ...r, promised: e.target.checked })} />
                Fecha comprometida con el cliente (no se recalcula)
              </label>
              <div className="flex justify-end gap-2">
                <Button variant="secondary" onClick={() => setReprog(null)}>Cancelar</Button>
                <Button disabled={!reprog.date} loading={busy === reprog.o.id} onClick={saveReprog}>Guardar</Button>
              </div>
            </div>
          </DialogContent>
        )}
      </Dialog>
    </div>
  );
}

export default Taller;
